import { SearchOutlined } from '@ant-design/icons';
import { startCase } from 'lodash';
import { useController, useForm } from 'react-hook-form';

import { Form, InputGroup } from '../../../bootstrap';
import { EnumFilterKeys, FilterState } from '../../hooks/useTableState';
import { ButtonLink } from '../Button/ButtonLink';
import { ControlledInput } from '../FormElements';

import s from './MultiSelectFilter.module.scss';

export const MultiSelectFilterStyles = s;

type MultiSelectFilterOpts<T extends FilterState, K extends EnumFilterKeys<T>> = {
  onlyFilterEnabled?: boolean;
  selectAllEnabled?: boolean;
  searchEnabled?: boolean;
  labelMappingFn?: (subKey: keyof NonNullable<T[K]>) => string;
  sectionMappingFn?: (index: number) => string | undefined;
  overrideValues?: (keyof NonNullable<T[K]>)[];
  selectAllByClearing?: boolean;
};

export const MultiSelectFilter = <T extends FilterState, K extends EnumFilterKeys<T>>(
  filterState: T,
  toggleFilterValue: (key: K, value: keyof NonNullable<T[K]>, clearOthers?: boolean) => void,
  selectAllValues: (key: K, selectAllByClearing?: boolean) => void,
  key: K,
  opts: MultiSelectFilterOpts<T, K> = {},
): (() => React.ReactNode) =>
  function MultiSelectFilterWrapper() {
    return (
      <MultiSelectFilterImpl
        filterState={filterState}
        toggleFilterValue={toggleFilterValue}
        selectAllValues={selectAllValues}
        filterKey={key}
        opts={opts}
      />
    );
  };

function MultiSelectFilterImpl<T extends FilterState, K extends EnumFilterKeys<T>>({
  filterState,
  toggleFilterValue,
  selectAllValues,
  filterKey,
  opts,
}: {
  filterState: T;
  toggleFilterValue: (key: K, value: keyof NonNullable<T[K]>, clearOthers?: boolean) => void;
  selectAllValues: (key: K, selectAllByClearing?: boolean) => void;
  filterKey: K;
  opts: MultiSelectFilterOpts<T, K>;
}): React.ReactNode {
  const { onlyFilterEnabled, selectAllEnabled, searchEnabled } = opts;

  const { control } = useForm<{ search: string }>({ defaultValues: { search: '' } });
  const { field: searchValue } = useController({ name: 'search', control });

  const labelMappingFn = (subKey: string) =>
    opts.labelMappingFn ? opts.labelMappingFn(subKey as keyof T[K]) : startCase(subKey);

  const keysAndValues = useKeysAndValues(filterState[filterKey], opts.overrideValues);
  const allSelectedByClearing = opts.selectAllByClearing && filterState[filterKey] == null;

  return (
    <Form>
      {selectAllEnabled && (
        <div className="mb-2">
          <ButtonLink type="button" onClick={() => selectAllValues(filterKey, opts.selectAllByClearing)}>
            Select All
          </ButtonLink>
        </div>
      )}

      {searchEnabled && (
        <InputGroup className="mb-2">
          <ControlledInput fieldName="search" control={control} inputClassnames="u-fs-2" placeholder="Search" />
          <InputGroup.Text>
            <SearchOutlined />
          </InputGroup.Text>
        </InputGroup>
      )}

      {keysAndValues.map(([subKey, value], index) => {
        const label = subKey === 'null' ? 'None' : labelMappingFn(subKey);

        if (!label.toLowerCase().includes(searchValue.value.toLowerCase())) {
          return null;
        }

        return (
          <>
            {opts.sectionMappingFn && opts.sectionMappingFn(index) && (
              <div className="mt-2 mb-1 u-color-bark3 u-fs-1">{opts.sectionMappingFn(index)}</div>
            )}
            <Form.Check
              id={`multi-select-filter${String(filterKey)}-${subKey}`}
              key={subKey}
              type="checkbox"
              label={
                <div className={s.labelContainer}>
                  <span className={s.label}>{label}</span>
                  {onlyFilterEnabled && (
                    <ButtonLink
                      className={s.only}
                      type="button"
                      onClick={() => toggleFilterValue(filterKey, subKey as keyof T[K], true)}
                      style={{ paddingLeft: '20px' }}
                    >
                      Only
                    </ButtonLink>
                  )}
                </div>
              }
              checked={(value || allSelectedByClearing) ?? false}
              onChange={() => toggleFilterValue(filterKey, subKey as keyof T[K])}
              className={s.multiSelectFilter}
            />
          </>
        );
      })}
    </Form>
  );
}

function useKeysAndValues<T extends FilterState, K extends EnumFilterKeys<T>>(
  fromFilterState: T[K],
  overrideValues?: (keyof NonNullable<T[K]>)[],
): [string, boolean][] {
  if (overrideValues) {
    return overrideValues.map((value): [string, boolean] => [
      value as string,
      (fromFilterState as Record<keyof NonNullable<T[K]>, boolean>)?.[value] ?? false,
    ]);
  }

  return Object.entries(fromFilterState ?? {});
}
